import {
  ApostleClass,
  ApostleClassType,
  ApostleFilterInput
} from '@apeiron/apostle'
import { styled } from '@mui/material'
import {
  ChipIcon,
  ChipLabel,
  ChipToggleButton,
  ChipToggleButtonGroup
} from '@src/components/share/filter/ChipToggle'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle,
  FilterTitle
} from '@src/components/share/filter/FilterSection'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import useFetchApostleFilterJson from '@src/hooks/json/useFetchApostleFilterJson'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const FILTER_FIELDS: (keyof ApostleFilterInput)[] = ['class']

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding: 10px 10px 10px 24px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }
`

const Title = FilterTitle

const ChipGroup = styled(ChipToggleButtonGroup)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const ChipButton = styled(ChipToggleButton)`
  font-size: 14px;
  padding: 4px 6px;
`

export const ApostleClassChipGroupView = ({
  className,
  selectedValue,
  onChange,
  options
}: {
  className?: string
  selectedValue?: ApostleClassType[]
  onChange: (event: any, value: ApostleClassType[]) => void
  options: ApostleClass[]
}) => {
  return (
    <ChipGroup className={className} value={selectedValue} onChange={onChange}>
      {R.map((apostleClass: ApostleClass) => {
        return (
          <ChipButton key={apostleClass.type} value={apostleClass.type}>
            <ChipIcon src={apostleClass.icon} />
            <ChipLabel>{apostleClass.name}</ChipLabel>
          </ChipButton>
        )
      }, options || [])}
    </ChipGroup>
  )
}

export const ApostleFilterClassGroup = ({
  className,
  selectedValue,
  onSelectionsChange,
  showTitle = true
}: ClassGroupProps) => {
  const { data } = useFetchApostleFilterJson()

  const clipFilter = data?.class

  const handleOnChange = useCallback(
    (_: any, value: ApostleClassType[]) => {
      onSelectionsChange(value)
    },
    [onSelectionsChange]
  )

  return (
    <>
      {showTitle && <Title>{clipFilter?.name || ''}</Title>}
      <ApostleClassChipGroupView
        options={clipFilter?.values || []}
        selectedValue={selectedValue}
        onChange={handleOnChange}
        className={className}
      />
    </>
  )
}

const ApostleClassFilter: FC<Props> = (props: Props) => {
  const { className, filterInput, onFilterChange } = props

  const { t } = useTranslation()

  const numberOfFilters = useMemo(() => {
    const countFilters = R.pipe<any, any, Object, unknown[][], number>(
      R.reject(R.isNil),
      R.pick(FILTER_FIELDS),
      R.toPairs,
      R.length
    )

    const numberOfClassFilters = countFilters(filterInput)

    return numberOfClassFilters
  }, [filterInput])

  const handleOnReset = useCallback(() => {
    onFilterChange(R.omit(FILTER_FIELDS, filterInput))
  }, [filterInput, onFilterChange])

  const handleSelectionChange = useCallback(
    (value: ApostleClassType[]) => {
      onFilterChange({ ...filterInput, class: value })
    },
    [filterInput]
  )

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>{t('apostle.filter.class')}</FilterSectionTitle>
        <ResetFilter filterCount={numberOfFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        <ApostleFilterClassGroup
          selectedValue={filterInput.class || []}
          onSelectionsChange={handleSelectionChange}
          className={className}
        />
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterInput: ApostleFilterInput
  onFilterChange: (input: ApostleFilterInput) => void
}

type ClassGroupProps = {
  className?: string
  onSelectionsChange: (value: ApostleClassType[]) => void
  selectedValue?: ApostleClassType[]
  showTitle?: boolean
}

export default ApostleClassFilter
